import { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { 
  TextFieldCustom,
  SelectField,
  DateField
} from "./CustomInputs";

const ExpensesForm = ({ initialValues, handleFormSubmit, buttonLabel }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [employees, setEmployees] = useState([]);
  const apiUrl = "https://oven-backend.onrender.com";

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${apiUrl}/employees/role/customer`);
        setEmployees(response.data);
      } catch (error) {
        console.error(t('error_fetching_employees'), error);
      }
    };

    fetchEmployees();
  }, [t]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={expensesSchema(t)}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit}>
          <Box
            display="grid"
            gap="30px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            sx={{
              "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <SelectField
              name="employee_id"
              label={t('employee')}
              formik={formik}
              options={employees.map(emp => ({
                value: emp._id,
                labelKey: emp.name
              }))}
              grid={2}
            />

            <TextFieldCustom
              name="category"
              label={t('category')}
              formik={formik}
              grid={2}
            />

            <TextFieldCustom
              name="amount"
              label={t('amount')}
              formik={formik}
              type="number"
              grid={2}
            />

            <DateField
              name="date"
              label={t('expense_date')}
              formik={formik}
              grid={2}
            />

            <SelectField
              name="payment_status"
              label={t('payment_status')}
              formik={formik}
              options={[
                { value: 'unpaid', labelKey: 'unpaid' },
                { value: 'paid', labelKey: 'paid' }
              ]}
              grid={2}
            />

            <SelectField
              name="inventory_status"
              label={t('inventory_status')}
              formik={formik}
              options={[
                { value: 'pending', labelKey: 'pending' },
                { value: 'cleared', labelKey: 'cleared' }
              ]}
              grid={2}
            />

            <TextFieldCustom
              name="description"
              label={t('description')}
              formik={formik}
              grid={4}
              multiline
              rows={4}
            />
          </Box>

          <Box display="flex" justifyContent="end" mt="20px">
            <Button 
              type="submit" 
              color="secondary" 
              variant="contained"
              sx={{ 
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Roboto, sans-serif',
                minWidth: '120px'
              }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

const expensesSchema = (t) => yup.object().shape({
  employee_id: yup.string().required(t('required_employee')),
  category: yup.string().required(t('required_category')),
  amount: yup.number()
    .positive(t('positive_number'))
    .required(t('required_amount')),
  description: yup.string().required(t('required_description')),
  date: yup.string().required(t('required_date')),
  payment_status: yup.string()
    .oneOf(["unpaid", "paid"], t('invalid_payment_status'))
    .required(t('required_payment_status')),
  inventory_status: yup.string()
    .oneOf(["cleared", "pending"], t('invalid_inventory_status'))
    .required(t('required_inventory_status')),
});

export default ExpensesForm;